import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import { useDeleteDashboard } from '../mutations'
import type { DashboardListItem } from '../types'

interface DeleteDashboardDialogProps {
  /** The dashboard to delete, or null when the dialog is closed. */
  dashboard: DashboardListItem | null
  onClose: () => void
}

/**
 * Confirmation modal for deleting a dashboard. Shared by the grid's row menu and, later, the
 * edit drawer.
 */
export function DeleteDashboardDialog({ dashboard, onClose }: DeleteDashboardDialogProps) {
  const { t } = useTranslation('admin-console')
  const { enqueueSnackbar } = useSnackbar()
  const deleteDashboard = useDeleteDashboard()

  const handleConfirm = () => {
    if (!dashboard) return
    deleteDashboard.mutate(
      { id: dashboard.id },
      {
        onSuccess: () => {
          enqueueSnackbar(t('dashboards.table.dashboardDeleted'), { variant: 'success' })
          onClose()
        },
      },
    )
  }

  return (
    <Dialog open={dashboard !== null} onClose={onClose}>
      <DialogTitle>{t('dashboards.delete.title')}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {t('dashboards.delete.confirm', { name: dashboard?.name ?? '' })}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('dashboards.delete.cancel')}</Button>
        <Button variant="contained" color="error" onClick={handleConfirm}>
          {t('dashboards.delete.delete')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
